import React, { useLayoutEffect } from "react";
// import { useTranslation } from "react-i18next";
import SplitType from "split-type";
import gsap from "gsap";
import OtherProjects from "../../components/Other-Projects/otherProjects";
import ProjectNav from "../../components/Project-Nav/projectNav";

export default function NFTProject() {
  // const { t } = useTranslation();

  useLayoutEffect(() => {
    const text = new SplitType(".words1", { types: "chars" });
    const textA = SplitType.create(".sentence1", { types: "words" });
    const textB = SplitType.create(".line1", { types: "lines" });

    gsap.fromTo(
      text.chars,
      { y: 0, opacity: 0 },
      { y: -12, opacity: 1, stagger: 0.03 }
    );
    gsap.fromTo(
      textA.words,
      { y: 0, opacity: 0, delay: 0.5 },
      { y: -12, opacity: 1, stagger: 0.06 }
    );
    gsap.fromTo(
      textB.lines,
      { y: 0, opacity: 0, delay: 0.5 },
      { y: -12, opacity: 1, stagger: 0.1 }
    );

    gsap.fromTo(
      ".box1",
      { y: 0, opacity: 0, delay: 1 },
      { y: -20, opacity: 2, duration: 1 }
    );

    gsap.config({
      force3D: false,
      nullTargetWarn: false,
      trialWarn: false,
    });
  });

  return (
    <div className="project-section case-study nft-project container mt-120">
      <div className="project-hero-card bg-secondary radius-20 w-100 p-40 sm:p-20 xs:p-20 box1">
        <div className="project-hero-title mb-20 sm:mb-12 xs:mb-8">
          <h1 className="f-40 xs:f-18 xs:l-h-24 sm:f-32 l-h-40 f-w-600 text-primary words1">
            MLB NFT
          </h1>
          <p className="f-16 xs:f-12 xs:l-h-20 sm:f-14 l-h-30 f-w-300 text-primary mt-8 sentence1">
            <span>NFT Platform · Mobile + Web · Calibraint Technologies · 3 months</span>
          </p>
        </div>
      </div>

      <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary mt-24 mb-20 mw-680 line1">
        An NFT marketplace for MLB baseball where fans and athletes can
        buy, sell, and trade player NFT cards. The most interesting design
        challenge was the card itself — I didn't want to use a generic
        rectangle like every other sports NFT platform. Derived the card
        shape from baseball field geometry instead. Also handled the
        frontend implementation in React.js with SVG-based dynamic card
        generation.
      </p>

      <div className="cs-pills">
        <span className="cs-pill">NFT / Web3</span>
        <span className="cs-pill">Mobile + Web</span>
        <span className="cs-pill">SVG Card System</span>
        <span className="cs-pill">Calibraint · 3 months</span>
      </div>

      <div className="cs-stats">
        <div className="cs-stat">
          <div className="cs-stat-n" style={{ color: "#f87171" }}>◆</div>
          <div className="cs-stat-l">Original card geometry</div>
        </div>
        <div className="cs-stat">
          <div className="cs-stat-n" style={{ color: "#fbbf24" }}>SVG</div>
          <div className="cs-stat-l">Dynamic generation</div>
        </div>
        <div className="cs-stat">
          <div className="cs-stat-n" style={{ color: "#38bdf8" }}>3mo</div>
          <div className="cs-stat-l">Design to launch</div>
        </div>
        <div className="cs-stat">
          <div className="cs-stat-n" style={{ color: "#4ade80" }}>✓</div>
          <div className="cs-stat-l">Client approved</div>
        </div>
      </div>

      <div className="mb-32">
        <div className="cs-lbl">The product</div>
        <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary line1">
          MLB NFT is a platform where baseball fans can buy, sell, and
          trade player NFT cards. Athletes share their best plays, cards
          are accessible for investment and collection, and everything
          runs on blockchain. The brief was clear — create NFT card
          designs that are easy to buy and sell, and build a landing page
          that makes fans actually want to engage with it.
        </p>
      </div>

      <div className="mb-32">
        <div className="cs-lbl">My role</div>
        <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary line1">
          UI/UX Designer and Developer. Brand identity, style guide, NFT
          card design system, card geometry exploration, landing page
          design, component library — and then the React.js frontend
          implementation with SVG card rendering.
        </p>
      </div>

      <div className="mb-8">
        <div className="cs-lbl">Research</div>
        <h3 className="f-20 l-h-28 f-w-600 text-primary mb-12 sentence1">
          What was wrong with every other sports NFT
        </h3>
        <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary mb-12 line1">
          Studied NBA Top Shot, Sorare, and a few other platforms. The
          consistent problem: every platform used the same rectangular
          card template with a photo dropped in. Nothing about the card
          said "this belongs to baseball" or any specific sport. It all
          felt like a generic Web3 product wearing a sports jersey.
        </p>
        <div className="cs-quote">
          <p>
            "If I'm paying for a digital collectible it should feel like it
            actually belongs to the sport — not just a rectangle with a
            photo on it."
          </p>
          <span>— Sports fan and NFT collector, research interview</span>
        </div>
      </div>

      <div className="mt-32 xs:mt-40 sm:mt-40 flex xs:flex-column">
        <div className="cs-img bg-secondary radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 mr-15 xs:mr-0 box1 nft-img13"></div>
        <div className="cs-img bg-secondary radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 ml-15 xs:ml-0 xs:mt-20 box1 nft-img1"></div>
      </div>

      <div className="mt-32 mb-8">
        <div className="cs-lbl">Pain points</div>
        <div className="cs-pains">
          <div className="cs-pain">
            <span className="cs-tag h">Critical</span>
            <div className="cs-pain-text">
              Every sports NFT used the same rectangular template — a
              basketball and baseball card looked identical except for the
              photo inside
            </div>
          </div>
          <div className="cs-pain">
            <span className="cs-tag h">Critical</span>
            <div className="cs-pain-text">
              Buying flows were complex for fans who were interested in
              NFTs but had no prior Web3 experience — wallet setup was a
              barrier before they could even see a card
            </div>
          </div>
          <div className="cs-pain">
            <span className="cs-tag h">Critical</span>
            <div className="cs-pain-text">
              Player stats and card data were static — no system for
              dynamically populating different players without a separate
              design asset per card
            </div>
          </div>
          <div className="cs-pain">
            <span className="cs-tag m">Medium</span>
            <div className="cs-pain-text">
              Landing pages led with technical Web3 language instead of
              sports passion — wrong starting point for a fan audience
            </div>
          </div>
          <div className="cs-pain">
            <span className="cs-tag m">Medium</span>
            <div className="cs-pain-text">
              Mobile was treated as an afterthought despite sports fans
              primarily being on mobile
            </div>
          </div>
        </div>
      </div>

      <div className="mt-32 mb-8">
        <div className="cs-lbl">Design decisions</div>
        <div className="cs-two-col">
          <div className="cs-card box1">
            <h4>Baseball diamond as the card shape</h4>
            <p>
              Took the baseball field's diamond geometry and built the
              card shape around it. Player image and stats sit within the
              diamond structure. Directly connected to the sport —
              something no other platform had done. This was the decision
              that differentiated everything else.
            </p>
          </div>
          <div className="cs-card box1">
            <h4>MLB colours, not generic Web3 palette</h4>
            <p>
              Navy, crimson, white — rooted in MLB's actual visual
              identity. Applied across cards, marketplace, and landing
              page. Fans recognised it immediately and it made the product
              feel authentic.
            </p>
          </div>
          <div className="cs-card box1">
            <h4>SVG-based dynamic cards</h4>
            <p>
              Built cards as SVG structures so player name, image, stats,
              collection name, and price can be populated from the
              database. No separate design file needed per player — solved
              the biggest production problem from the start.
            </p>
          </div>
          <div className="cs-card box1">
            <h4>Landing page for fans first</h4>
            <p>
              Led with baseball — the atmosphere, the plays, the players —
              not wallet instructions and token economics. Fan-first entry
              creates emotional buy-in before you introduce the NFT
              mechanics.
            </p>
          </div>
        </div>
      </div>

      <div className="cs-img bg-secondary radius-20 minh-600 sm:minh-450 xs:minh-200 mt-32 box1 nft-img2"></div>

      <div className="mt-32 xs:mt-40 sm:mt-40 flex xs:flex-column">
        <div className="cs-img bg-secondary radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 mr-15 xs:mr-0 box1 nft-img3"></div>
        <div className="cs-img bg-secondary radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 mr-15 ml-15 xs:ml-0 xs:mr-0 xs:mt-20 box1 nft-img4"></div>
        <div className="cs-img bg-secondary radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 ml-15 xs:ml-0 xs:mt-20 box1 nft-img5"></div>
      </div>

      <div className="cs-img bg-white radius-20 minh-600 sm:minh-450 xs:minh-200 mt-32 box1 nft-img6"></div>

      <div className="mt-32 xs:mt-40 sm:mt-40 flex xs:flex-column">
        <div className="cs-img bg-secondary radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 mr-15 xs:mr-0 box1 nft-img8"></div>
        <div className="cs-img bg-secondary radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 mr-15 ml-15 xs:ml-0 xs:mr-0 xs:mt-20 box1 nft-img9"></div>
        <div className="cs-img bg-secondary radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 ml-15 xs:ml-0 xs:mt-20 box1 nft-img10"></div>
      </div>

      <div className="mt-32 xs:mt-40 sm:mt-40 flex xs:flex-column">
        <div className="cs-img bg-secondary radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 mr-15 xs:mr-0 box1 nft-img11"></div>
        <div className="cs-img bg-secondary radius-20 flex-1 minh-400 sm:minh-280 xs:minh-200 ml-15 xs:ml-0 xs:mt-20 box1 nft-img12"></div>
      </div>

      <div className="mt-32 mb-32">
        <div className="cs-lbl">Handoff &amp; build</div>
        <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary line1">
          Grouped every SVG layer so developers could inject dynamic data
          cleanly. Documented exactly how each element — player image,
          name, stats — mapped to its SVG layer. Used base64 URL encoding
          for the Qt-based image rendering challenge the dev team faced.
          Clean, unambiguous handoff from day one.
        </p>
      </div>

      <div className="mb-8">
        <div className="cs-lbl">Outcomes</div>
        <div className="cs-outcomes">
          <div className="cs-outcome">
            <div className="ico">◆</div>
            <h4>Original card design</h4>
            <p>
              Diamond geometry — ground-up original, no template
              reference. Directly from the baseball field.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">⚙️</div>
            <h4>Dynamic SVG system</h4>
            <p>
              Cards fully dynamic — all player data populated from
              database without manual design work per card.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">🏆</div>
            <h4>Client approved</h4>
            <p>
              Client evaluation positive after first test site. Delivered
              to launch readiness together with dev team.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">📱</div>
            <h4>Mobile + web</h4>
            <p>
              Full marketplace across both — mobile designed as a
              first-class experience, not a desktop adaptation.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">⚾</div>
            <h4>Fan-first landing</h4>
            <p>
              Landing page leading with baseball passion — converting
              sports fans without requiring Web3 knowledge upfront.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">💻</div>
            <h4>Built it too</h4>
            <p>
              Implemented SVG card rendering in React.js — eliminated
              translation errors between design and built output.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-32">
        <div className="cs-lbl">Reflection</div>
        <div className="cs-reflection-item">
          <span className="marker">→</span>
          <p>
            <strong>The geometry decision was the most important one.</strong>{" "}
            Choosing a diamond over a rectangle changed the entire visual
            language of the product. The big design decisions often happen
            before you open a frame — in how you conceptually frame what
            the thing is.
          </p>
        </div>
        <div className="cs-reflection-item">
          <span className="marker">→</span>
          <p>
            <strong>SVG made me a more precise designer.</strong> Every
            layer name, position, and grouping had to map to a real data
            field. Most technically demanding design work I'd done at that
            point — it made me think about implementation constraints from
            artboard one.
          </p>
        </div>
        <div className="cs-reflection-item">
          <span className="marker">→</span>
          <p>
            <strong>Knowing how to build is a design advantage.</strong>{" "}
            Understanding SVG rendering and base64 encoding meant I could
            solve the dynamic card problem directly instead of waiting for
            a developer workaround.
          </p>
        </div>
        <div className="cs-reflection-item">
          <span className="marker">→</span>
          <p>
            <strong>If I did this again:</strong> More thought into the
            card reveal animation — what does opening a pack actually feel
            like? The static card was strong but the reveal moment was
            underdesigned. That's where most of the emotional opportunity
            was left.
          </p>
        </div>
      </div>

      <div className="project-bottom-nav mt-60 mb-100 flex justify-between align-center box1">
        <div className="flex xs:justify-center">
          <OtherProjects active="nft" />
        </div>
        <ProjectNav current="nft" />
      </div>
    </div>
  );
}
